import { FormEvent, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { StatCard, CreateEntityModal, Modal, useToast, LifecycleBar, EntityNotesPanel, EntityGroupsPanel, LinkedDocumentsPanel, LastUpdated, TaxBillCollectorGroups } from '../organisms';
import { EntityHeader } from '../shell';
import { formatDate } from '../lib/orgTimezone';
import { useEntityPermission } from '../lib/usePermissions';
import { billYearLabel } from '../lib/billYear';
import { STATE_SELECT_OPTIONS } from '../lib/us-states';
import { userNameFrom, type DirectoryUser } from '../lib/userDirectory';

/** Dollars, rounded, with thousands separators (matches the dashboard money format). */
const money = (v: number | null | undefined) => (v != null ? `$${Math.round(v).toLocaleString()}` : '—');

/**
 * §10 — a lifecycle-stage stamp: the date the bill entered a stage + who moved it
 * there. Renders "—" until the stage happens, then "Jul 24, 2026 · jane@firm.com".
 */
function StageStamp({ date, by }: { date: string | null; by: string | null }) {
  if (!date) return <span style={{ color: 'var(--color-muted)' }}>—</span>;
  return (
    <span>
      {formatDate(date)}
      {by && <span style={{ color: 'var(--color-ink-3)' }}> · {by}</span>}
    </span>
  );
}

/** A brand-colored inline link (used across the tax-bill ID box, §7). */
function LinkText({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <a onClick={onClick} style={{ color: 'var(--brand-primary)', cursor: 'pointer', fontWeight: 'var(--fw-medium)' }}>{label}</a>
  );
}

/** One-line collector mailing address (§7); em-dash when nothing is on file. */
function collectorAddressText(c: {
  mailing_address_line1: string | null; mailing_address_line2: string | null;
  mailing_city: string | null; mailing_state: string | null; mailing_postal_code: string | null;
} | undefined): string {
  if (!c) return '—';
  const line1 = [c.mailing_address_line1, c.mailing_address_line2].filter(Boolean).join(' ');
  const cityLine = [c.mailing_city, [c.mailing_state, c.mailing_postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const out = [line1, cityLine].filter(Boolean).join(', ');
  return out || '—';
}

/**
 * TaxBillDetail — /v2/tax-bills/:id
 *
 * Bespoke detail with the tax-bill workflow buttons (receive →
 * process → approve), all hitting `POST /api/workflow/process-bill/:id`
 * with `{ action: <action.name> }`.
 *
 * Pay + void are now wired to the shipped workflow endpoints:
 *   - POST /api/workflow/tax-bills/:billId/pay
 *       body { payment_date, payment_amount (dollars), payment_method?, payment_reference? }
 *       valid from received/processed/approved → paid (server CAS, lifecycle-4).
 *   - POST /api/workflow/tax-bills/:billId/void
 *       body { void_reason } — valid from any non-terminal status → void.
 *
 * Phase 11 Block 10 PR 2 (2026-05-12). Pay/void wired (backlog 15.2).
 */

// Status enum mirrors src/config/enums.js TAX_BILL_STATUS_FLOW +
// src/db/schema.sql tax_bills.status CHECK — the server's actual set.
type BillStatus = 'no_activity' | 'received' | 'processed' | 'approved' | 'transmitted' | 'paid' | 'void';

interface TaxBill {
  id: string;
  organization_id: string;
  parcel_id: string;
  collector_id: string;
  updated_by?: string | null;
  updated_at?: string | null;
  tax_year: number;
  process_year: number | null; // RH§1 — the PAYABLE year (Siger "Process")
  status: BillStatus;
  account_number: string | null;
  tax_amount: number | null;
  due_date: string | null;
  received_date: string | null;
  approved_date: string | null;
  approved_by: string | null;
  /** 'standard' | 'corrected' | 'refund'. A correction/refund bill is ISSUED at
   *  `transmitted` and carries no earlier lifecycle stamps — see the LifecycleBar
   *  stages below.
   *
   *  The column is `TEXT NOT NULL DEFAULT 'standard'` (schema.sql:996, migration
   *  126:29), so it is never null on a fresh OR a migrated database. The `| null`
   *  here and the `|| 'standard'` below are belt-and-braces against a partial
   *  payload — do NOT read null as meaning anything. */
  payment_date: string | null;
  payment_amount: number | null;
  payment_method: string | null;
  payment_reference: string | null;
  void_reason: string | null;
  voided_at: string | null;
  // §10 — date + who for every lifecycle stage
  received_by: string | null;
  processed_date: string | null;
  processed_by: string | null;
  transmitted_date: string | null;
  transmitted_by: string | null;
  paid_by: string | null;
  voided_by: string | null;
  // §4.7 — a corrected/refund bill is a NEW bill on this same lifecycle; bill_type
  // distinguishes it and original_bill_id links it back. Neither edits the original.
  // A NULL bill_type predates migration 126 and reads as 'standard'.
  bill_type: BillType | null;
  original_bill_id: string | null;
  // Set on the ORIGINAL when a corrected bill supersedes it. Deliberately NOT the
  // lifecycle `status` (§4.5 keeps that unchanged) — this is a separate marker.
  correction_status: string | null;
}

interface Parcel { id: string; account_number: string; name: string | null; property_id: string | null; }
interface Property { id: string; name: string | null; client_id: string | null; }
interface Client { id: string; name: string | null; }
// §7 — the collector's mailing address is shown beneath the collector on the tax
// bill and is editable here; saving writes back to the collector record itself.
interface Collector {
  id: string;
  name: string;
  mailing_address_line1: string | null;
  mailing_address_line2: string | null;
  mailing_city: string | null;
  mailing_state: string | null;
  mailing_postal_code: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
}

const statusVariant: Record<BillStatus, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  no_activity: 'neutral',
  received: 'warning',
  processed: 'info',
  approved: 'success',
  transmitted: 'info',
  paid: 'success',
  void: 'danger',
};

type BillAction = 'receive' | 'process' | 'approve' | 'transmit';

// §4.7 — the three bill types. A refund carries NEGATIVE line amounts; a positive
// delta through the same path is a SUPPLEMENTAL (no separate type by design).
type BillType = 'standard' | 'corrected' | 'refund';

/** A NULL bill_type predates migration 126 and means 'standard'. */
const billTypeOf = (b: { bill_type: BillType | null }): BillType => b.bill_type || 'standard';

const BILL_TYPE_LABEL: Record<BillType, string> = {
  standard: 'Standard',
  corrected: 'Corrected statement',
  refund: 'Refund',
};

// A correction is issued FROM a transmitted or paid standard bill — mirrors
// CORRECTABLE_STATUSES in src/services/bill-corrections.js. Keep the two in step:
// offering the action on a status the service refuses is a guaranteed 400.
const CORRECTABLE_FROM: BillStatus[] = ['transmitted', 'paid'];

// C3 — the transmittal covering this bill + its portal-share state.
interface BillTransmittal { id: string; status: string; document_id: string | null; shared: boolean }
// C1 — a same-state, not-yet-transmitted bill offered for catch-up after a transmit.
interface CatchUpBill { id: string; account_number: string; tax_year: number; property_name: string; collector_name: string | null; state: string }
interface ProcessBillResult { skipped?: boolean; reason?: string; transmittal?: BillTransmittal }

// Proper past-tense labels for the success toast — naive `${action}d` yields
// the broken "processd"/"transmitd" for two of the four transitions.
const BILL_ACTION_PAST: Record<BillAction, string> = {
  receive: 'received',
  process: 'processed',
  approve: 'approved',
  transmit: 'transmitted',
};

// Map current status → next valid action (matches src/config/actions.js tax_bills entry).
// §8.2: an approved bill advances to 'transmitted' (transmittal is a bill state).
const nextAction: Partial<Record<BillStatus, { action: BillAction; label: string }>> = {
  no_activity: { action: 'receive', label: 'Confirm & Receive' },
  received: { action: 'process', label: 'Verify & Process' },
  processed: { action: 'approve', label: 'Approve' },
  approved: { action: 'transmit', label: 'Mark Transmitted' },
};

// Pay is valid from received/processed/approved/transmitted (src/services/workflows.js payBill validFrom).
const PAYABLE_FROM: BillStatus[] = ['received', 'processed', 'approved', 'transmitted'];
// Void is valid from any non-terminal status (server voidBill terminal = paid/void).
const VOIDABLE_FROM: BillStatus[] = ['no_activity', 'received', 'processed', 'approved', 'transmitted'];

export function TaxBillDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<BillAction | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [collectorAddrOpen, setCollectorAddrOpen] = useState(false);
  // Capability gates (hooks — declared here, before any early return). Mirror the
  // endpoints: Edit → PUT tax-bills (tax_bill.can_edit); Pay/Void/process → the
  // /tax-bills workflow (tax_bill.can_process). Without these the buttons rendered on
  // bill STATUS alone — over-exposed to view-only roles (training) + dead 403s for agent.
  const canEditBill = useEntityPermission('tax_bill', 'can_edit');
  const canProcessBill = useEntityPermission('tax_bill', 'can_process');
  // C3 — the share-on-create pop-up shown after Transmit files a (not-yet-shared) transmittal.
  const [shareOnCreate, setShareOnCreate] = useState<{ id: string; document_id: string | null } | null>(null);
  // C1 — after a transmit, offer to catch up the client's other approved same-state bills.
  // The share pop-up is deferred behind it so the two modals don't stack.
  const [catchUp, setCatchUp] = useState<{ bills: CatchUpBill[]; state: string } | null>(null);
  const [catchUpSel, setCatchUpSel] = useState<Set<string>>(new Set());
  const [pendingShare, setPendingShare] = useState<BillTransmittal | null>(null);

  const billQuery = useQuery<TaxBill>({
    queryKey: ['tax_bills', id],
    queryFn: ({ signal }) => api.get<TaxBill>(`/api/tax-bills/${id}`, signal),
    enabled: !!id,
  });

  // §4.7 — the corrected/refund bills issued against THIS bill. Drives both the
  // "already corrected" notice and whether "Issue correction" is offered at all.
  // Relies on the original_bill_id fk filter in routes/crud.js: without it the param
  // is dropped silently and this returns the org's whole bill list.
  const correctionsQuery = useQuery<TaxBill[]>({
    queryKey: ['bill-corrections-of', id],
    queryFn: ({ signal }) => api.get<TaxBill[]>(`/api/tax-bills?original_bill_id=${id}`, signal),
    enabled: !!id,
  });
  const issuedCorrections = correctionsQuery.data;

  // The bill this one corrects, when it IS a correction — so the pair is navigable
  // from both ends (the §4.7 audit link is only useful if it is reachable).
  const originalQuery = useQuery<TaxBill>({
    queryKey: ['tax_bills', billQuery.data?.original_bill_id],
    queryFn: ({ signal }) => api.get<TaxBill>(`/api/tax-bills/${billQuery.data!.original_bill_id}`, signal),
    enabled: !!billQuery.data?.original_bill_id,
  });

  const parcelQuery = useQuery<Parcel>({
    queryKey: ['parcel', billQuery.data?.parcel_id],
    queryFn: ({ signal }) => api.get<Parcel>(`/api/parcels/${billQuery.data!.parcel_id}`, signal),
    enabled: !!billQuery.data?.parcel_id,
  });

  const collectorQuery = useQuery<Collector>({
    queryKey: ['collector', billQuery.data?.collector_id],
    queryFn: ({ signal }) => api.get<Collector>(`/api/collectors/${billQuery.data!.collector_id}`, signal),
    enabled: !!billQuery.data?.collector_id,
  });

  // §7 — resolve the parcel's property + the property's client so the ID box can
  // link client → property → parcel, not just the parcel.
  const propertyQuery = useQuery<Property>({
    queryKey: ['property', parcelQuery.data?.property_id],
    queryFn: ({ signal }) => api.get<Property>(`/api/properties/${parcelQuery.data!.property_id}`, signal),
    enabled: !!parcelQuery.data?.property_id,
  });
  const clientQuery = useQuery<Client>({
    queryKey: ['client', propertyQuery.data?.client_id],
    queryFn: ({ signal }) => api.get<Client>(`/api/clients/${propertyQuery.data!.client_id}`, signal),
    enabled: !!propertyQuery.data?.client_id,
  });

  // B2 — the lifecycle _by columns store the actor's EMAIL; resolve to their name
  // for display (falls back to the email if unresolved).
  const usersQuery = useQuery<DirectoryUser[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get('/api/users-list', signal),
  });
  const nameFor = (who: string | null): string | null => userNameFrom(usersQuery.data, who);

  const mutation = useMutation<ProcessBillResult, Error, BillAction>({
    mutationFn: (action: BillAction) =>
      api.post<ProcessBillResult>(`/api/workflow/process-bill/${id}`, { action }),
    onSuccess: async (data, action) => {
      // The backend returns HTTP 200 with {skipped:true} when the org has bill
      // transmittal disabled — the transition is a no-op, so surface it as a
      // warning (not a green success) and skip the misleading refetch.
      if (data && data.skipped) {
        toast({ variant: 'warning', message: data.reason || 'No change — bill transmittal is disabled for this organization.' });
        setConfirmAction(null);
        return;
      }
      toast({ variant: 'success', message: `Bill ${BILL_ACTION_PAST[action]}.` });
      queryClient.invalidateQueries({ queryKey: ['tax_bills', id] });
      queryClient.invalidateQueries({ queryKey: ['tax-bills-list'] });
      queryClient.invalidateQueries({ queryKey: ['transmittal-by-bill', id] });
      setConfirmAction(null);
      // C3/C1 — Transmit filed a (not-yet-shared) transmittal. Before the share pop-up,
      // check for the client's OTHER approved same-state bills and offer to catch them up.
      if (data && data.transmittal) {
        try {
          const cands = await api.get<CatchUpBill[]>(`/api/transmittals/catch-up/${id}`);
          if (cands.length) {
            setCatchUp({ bills: cands, state: cands[0].state });
            setCatchUpSel(new Set(cands.map((c) => c.id)));
            setPendingShare(data.transmittal);
            return;
          }
        } catch { /* best-effort — fall through to the share pop-up */ }
        setShareOnCreate(data.transmittal);
      }
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  // C1 — reveal the deferred share pop-up for the original bill once catch-up is resolved.
  const finishCatchUp = () => {
    setCatchUp(null);
    const share = pendingShare;
    setPendingShare(null);
    if (share) setShareOnCreate(share);
  };
  // C1 — transmit the selected same-state bills (each auto-assembles its own transmittal),
  // best-effort per bill, then reveal the deferred share pop-up.
  const catchUpMut = useMutation<{ done: number; failed: number }, Error, string[]>({
    mutationFn: async (ids) => {
      let done = 0, failed = 0;
      for (const bid of ids) {
        try { await api.post(`/api/workflow/process-bill/${bid}`, { action: 'transmit' }); done++; }
        catch { failed++; }
      }
      return { done, failed };
    },
    onSuccess: ({ done, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['tax-bills-list'] });
      queryClient.invalidateQueries({ queryKey: ['transmittals'] });
      if (done) toast({ variant: 'success', message: `${done} more ${done === 1 ? 'bill' : 'bills'} transmitted${failed ? ` · ${failed} skipped` : ''}.` });
      else if (failed) toast({ variant: 'error', message: `Couldn't transmit the selected ${failed === 1 ? 'bill' : 'bills'}.` });
      finishCatchUp();
    },
    onError: () => { toast({ variant: 'error', message: 'Catch-up failed.' }); finishCatchUp(); },
  });

  // C3 — the transmittal covering this bill, surfaced on the profile with a share toggle.
  const transmittalQuery = useQuery<BillTransmittal | null>({
    queryKey: ['transmittal-by-bill', id],
    queryFn: ({ signal }) => api.get<BillTransmittal | null>(`/api/transmittals/by-bill/${id}`, signal),
    enabled: !!id,
  });
  const shareMut = useMutation<unknown, Error, { docId: string; share: boolean }>({
    mutationFn: ({ docId, share }) => api.patch(`/api/documents/${docId}/share`, { notify_client: share }),
    onSuccess: (_d, vars) => {
      toast({ variant: vars.share ? 'success' : 'info', message: vars.share ? 'Transmittal shared to the client portal.' : 'Transmittal unshared from the portal.' });
      queryClient.invalidateQueries({ queryKey: ['transmittal-by-bill', id] });
    },
    onError: (err) => toast({ variant: 'error', message: err.message }),
  });

  if (billQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading bill…</p>;
  }
  if (billQuery.error) {
    const notFound = billQuery.error instanceof ApiClientError && billQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Tax bill not found' : 'Failed to load bill'}</h2>
        <p style={{ margin: 0 }}>{(billQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/tax-bills')}>
            Back to Tax Bills
          </Button>
        </p>
      </div>
    );
  }
  if (!billQuery.data) return null;

  const bill = billQuery.data;
  // A corrected / refund bill is issued straight to `transmitted`, so its earlier
  // lifecycle stamps are absent by construction (see the LifecycleBar stages below).
  //
  // ⚠️ TESTED AGAINST 'standard', NOT AGAINST THE KNOWN CORRECTION TYPES, and that
  // asymmetry is the point: an unrecognised future bill_type falls into the SILENT
  // branch rather than the remarking one. Listing `=== 'corrected' || === 'refund'`
  // would make a new fourth type start commenting on stamps nobody has decided it
  // should have. Same unknown-is-not-false rule the bar itself now follows.
  // ⚠️ TWO OF THE FIVE STAMPS CANNOT BE TRUSTED TO MEAN ANYTHING BY THEIR ABSENCE.
  // `processed_date` and `transmitted_date` arrived in migration 109 with NO backfill
  // and are written by exactly one path (`processBill`, workflows.js:591,597).
  // `seed-demo.js` writes received / approved / payment and never those two. So on a
  // bill that never went through the workflow — seeded, imported, backfilled — those
  // two columns are empty BY CONSTRUCTION and say nothing about what happened.
  //
  // This gates ONLY those two, on positive evidence that this bill is stamped at all.
  // Deliberately not a blanket gate over all five: `received_date` / `approved_date` /
  // `payment_date` are older, seeded columns, so their absence IS meaningful and stays
  // reportable. Measured on the live demo — 81 non-void standard bills, of which the
  // note fired on **51**; gating all five drops it to 4, gating these two drops it to
  // **5**. The extra bill is the point: it is a real gap (paid, no approved date) that
  // the blanket version would have hidden. Suppressing noise must not cost a signal.
  const stampedByWorkflow = !!(bill.processed_date || bill.transmitted_date);
  const next = nextAction[bill.status];
  const canPay = PAYABLE_FROM.includes(bill.status);
  const canVoid = VOIDABLE_FROM.includes(bill.status);

  // §4.7 — offer "Issue correction" only where the service would actually accept it:
  // a STANDARD bill (a corrected/refund bill cannot itself be corrected), in a
  // correctable status, with no correction already issued (the service allows one
  // per original and 409s on a second). `issuedCorrections` is undefined while the
  // query is in flight, so the control stays hidden until we know — an eager button
  // that 409s teaches the operator nothing.
  const billType = billTypeOf(bill);
  const canCorrect = billType === 'standard'
    && CORRECTABLE_FROM.includes(bill.status)
    && issuedCorrections !== undefined
    && issuedCorrections.length === 0;

  return (
    <>
      <EntityHeader
        prefix={`Tax bill · ${billYearLabel(bill.tax_year, bill.process_year)}`}
        title={parcelQuery.data ? `${parcelQuery.data.account_number}${parcelQuery.data.name ? `: ${parcelQuery.data.name}` : ''}` : 'Loading parcel…'}
        subtitle={<>{collectorQuery.data?.name ? `Collector: ${collectorQuery.data.name}` : null}<LastUpdated updatedBy={bill.updated_by} updatedAt={bill.updated_at} /></>}
        statusPills={
          <>
            <Badge variant={statusVariant[bill.status]} dot>{bill.status.replace(/_/g, ' ')}</Badge>
            {/* §4.7 — the bill's own type, shown only when it is NOT a plain standard
                bill. A refund's amounts are negative, so reading it as an ordinary
                bill misreads the sign; the pill is what makes that legible. */}
            {billType !== 'standard' && (
              <Badge variant={billType === 'refund' ? 'warning' : 'info'}>{BILL_TYPE_LABEL[billType]}</Badge>
            )}
            {/* Shown on the ORIGINAL once a corrected bill supersedes it. The
                lifecycle status deliberately does not move (§4.5), so without this
                the original looks like the live bill it no longer is. */}
            {bill.correction_status === 'see_corrected_statement' && (
              <Badge variant="warning">See corrected statement</Badge>
            )}
          </>
        }
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/tax-bills')}>
              Back
            </Button>
            {canEditBill && (
              <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                Edit all fields
              </Button>
            )}
            {canVoid && canProcessBill && (
              <Button variant="danger" size="sm" onClick={() => setVoidOpen(true)}>
                Void
              </Button>
            )}
            {canPay && canProcessBill && (
              <Button variant="secondary" size="sm" onClick={() => setPayOpen(true)}>
                Record Payment
              </Button>
            )}
            {canCorrect && canProcessBill && (
              <Button variant="secondary" size="sm" onClick={() => setCorrectionOpen(true)}>
                Issue correction
              </Button>
            )}
            {next && canProcessBill && (
              <Button variant="primary" size="sm" onClick={() => setConfirmAction(next.action)}>
                {next.label}
              </Button>
            )}
          </>
        }
        kpis={
          <>
            <StatCard
              label="Amount"
              value={money(bill.tax_amount)}
              caption={bill.process_year && bill.process_year !== bill.tax_year
                ? `Tax year ${bill.tax_year} · payable ${bill.process_year}`
                : `Tax year ${bill.tax_year}`}
              accent="hunter"
            />
            <StatCard
              label="Due"
              value={formatDate(bill.due_date)}
              caption={bill.received_date ? `Received ${formatDate(bill.received_date)}` : 'Not yet received'}
              accent="amber"
            />
            <StatCard
              label="Approval"
              value={formatDate(bill.approved_date)}
              caption={bill.approved_by ? `by ${nameFor(bill.approved_by)}` : 'Not yet approved'}
              accent={bill.approved_date ? 'green' : 'info'}
            />
            <StatCard
              label="Payment"
              value={bill.payment_date ? formatDate(bill.payment_date) : (bill.status === 'void' ? 'Voided' : '—')}
              caption={
                bill.status === 'paid'
                  ? 'Paid'
                  : bill.status === 'void' ? 'Bill voided' : 'Unpaid'
              }
              accent={bill.status === 'paid' ? 'green' : bill.status === 'void' ? 'red' : 'amber'}
            />
          </>
        }
      />

      {/* §4.7 — the audit link, rendered from whichever end of the pair you are on.
          Every bill snapshots the value it was calculated on; that only settles an
          argument if the two bills can actually be put side by side. */}
      {billType !== 'standard' && bill.original_bill_id && (
        <CorrectionNotice
          text={billType === 'refund'
            ? 'This refund bill was issued against an earlier bill.'
            : 'This corrected statement supersedes an earlier bill.'}
          linkLabel={originalQuery.data
            ? `View original — ${originalQuery.data.account_number || originalQuery.data.id.slice(0, 8)}`
            : 'View original bill'}
          onClick={() => navigate(`/tax-bills/${bill.original_bill_id}`)}
        />
      )}
      {issuedCorrections && issuedCorrections.length > 0 && (
        <CorrectionNotice
          text={`This bill has been superseded — ${issuedCorrections.length === 1 ? 'a' : issuedCorrections.length} ${
            issuedCorrections.length === 1 ? 'correction was' : 'corrections were'
          } issued against it. Its own amounts are unchanged.`}
          linkLabel={`View ${BILL_TYPE_LABEL[billTypeOf(issuedCorrections[0])].toLowerCase()}`}
          onClick={() => navigate(`/tax-bills/${issuedCorrections[0].id}`)}
        />
      )}

      <div style={{
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        background: 'var(--color-card)',
        padding: 'var(--sp-4) var(--sp-5)',
        margin: 'var(--sp-4) 0',
      }}>
        <div style={{
          fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-muted)', marginBottom: 'var(--sp-3)',
        }}>Lifecycle</div>
        <LifecycleBar
          // §5 — each step past the first declares the date that would exist if it
          // really happened, so the bar can't tick a step the record does not
          // support. `no_activity` is the starting state, not a step, so it has no
          // evidence field and is never questioned.
          //
          // ⚠️ A CORRECTION / REFUND BILL IS BORN `transmitted` — `insertCorrectionBill`
          // INSERTs it directly at that status. THREE stages are omitted, not four, and
          // the boundary is the whole point: those three are a DIFFERENT KIND OF ABSENT
          // from the fourth.
          //
          //   received / processed / approved — these steps NEVER HAPPENED. A correction
          //     does not pass through them. Nothing truthful can be said, so the key is
          //     omitted and the bar stays silent. Absent by construction.
          //   transmitted — this step DID happen; it is the bill's birth state. Only the
          //     DATE was never recorded. That is *unstamped*, not *never*, which is
          //     exactly what `evidence: null` means and what the bar states as a fact.
          //
          // Passing it is what SURFACES the missing issue date instead of concealing it —
          // and it makes the omission self-limiting: if `insertCorrectionBill` ever writes
          // a real `transmitted_date`, that stage simply starts showing it and none of
          // this needs revisiting, because the omission never covered that stage.
          // `paid` is never omitted either — a correction can genuinely be paid later.
          stages={[
            { key: 'no_activity', label: 'No activity' },
            { key: 'received', label: 'Received', ...(billType !== 'standard' ? {} : { evidence: bill.received_date }) },
            // The two un-backfilled, workflow-only columns (see `stampedByWorkflow`):
            // reportable only on a bill that shows it is stamped at all. A correction
            // is exempt — its `transmitted` genuinely happened and is the one thing
            // worth surfacing about it.
            { key: 'processed', label: 'Processed', ...(billType !== 'standard' || !stampedByWorkflow ? {} : { evidence: bill.processed_date }) },
            { key: 'approved', label: 'Approved', ...(billType !== 'standard' ? {} : { evidence: bill.approved_date }) },
            { key: 'transmitted', label: 'Transmitted', ...(billType === 'standard' && !stampedByWorkflow ? {} : { evidence: bill.transmitted_date }) },
            { key: 'paid', label: 'Paid', evidence: bill.payment_date },
          ]}
          current={bill.status}
          terminated={bill.status === 'void' ? { label: 'This bill was voided.' } : null}
        />
      </div>

      {/* §4.5 — the parcel-year's bills grouped by collector, each collector's
          jurisdiction lines carrying a reachable base (assessed → reduction →
          taxable → tax) with savings + rate_basis. A parcel-year can hold several
          bills (one per collector); this bill's collector group is marked "Viewing". */}
      <TaxBillCollectorGroups parcelId={bill.parcel_id} taxYear={bill.tax_year} highlightBillId={bill.id} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'var(--sp-4)',
      }}>
        <InfoCard label="Identification">
          <Row label="Bill ID" value={<span style={{ fontFamily: 'var(--font-mono)' }}>{bill.id.slice(0, 8)}…</span>} />
          <Row label="Account #" value={bill.account_number || '—'} />
          {/* RH§1 — the two years, each its own labeled line so it's never ambiguous. */}
          <Row label="Tax year" value={String(bill.tax_year)} />
          <Row label="Payable year" value={bill.process_year != null ? String(bill.process_year) : '—'} />
          {/* §7 — the full ownership chain, each linked: client → property → parcel. */}
          <Row label="Client" value={clientQuery.data ? (
            <LinkText label={clientQuery.data.name || '—'} onClick={() => navigate(`/clients/${clientQuery.data!.id}`)} />
          ) : '—'} />
          <Row label="Property" value={propertyQuery.data ? (
            <LinkText label={propertyQuery.data.name || '—'} onClick={() => navigate(`/properties/${propertyQuery.data!.id}`)} />
          ) : '—'} />
          <Row label="Parcel" value={parcelQuery.data ? (
            <LinkText label={`${parcelQuery.data.account_number} ↗`} onClick={() => navigate(`/parcels/${parcelQuery.data!.id}`)} />
          ) : '—'} />
          {/* §7 — collector is clickable to its profile; its mailing address shows
              beneath (labeled) and is editable here, writing back to the collector. */}
          <Row label="Collector" value={collectorQuery.data ? (
            <LinkText label={collectorQuery.data.name} onClick={() => navigate(`/collectors/${collectorQuery.data!.id}`)} />
          ) : '—'} />
          <Row
            label="Collector mailing address"
            value={
              <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                <span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'baseline', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span>{collectorAddressText(collectorQuery.data)}</span>
                  {collectorQuery.data && (
                    <button
                      type="button"
                      onClick={() => setCollectorAddrOpen(true)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-primary)', font: 'inherit', textDecoration: 'underline' }}
                    >Edit</button>
                  )}
                </span>
                {/* B2 — editing the mailing address stamps who updated it + when. */}
                {collectorQuery.data && (collectorQuery.data.updated_by || collectorQuery.data.updated_at) && (
                  <LastUpdated updatedBy={collectorQuery.data.updated_by} updatedAt={collectorQuery.data.updated_at} />
                )}
              </span>
            }
          />
        </InfoCard>
        <InfoCard label="Lifecycle">
          <Row label="Status" value={<Badge variant={statusVariant[bill.status]} dot>{bill.status.replace(/_/g, ' ')}</Badge>} />
          {/* §10 — each stage that has happened shows its date + who moved it there. */}
          <Row label="Received" value={<StageStamp date={bill.received_date} by={nameFor(bill.received_by)} />} />
          <Row label="Processed" value={<StageStamp date={bill.processed_date} by={nameFor(bill.processed_by)} />} />
          <Row label="Approved" value={<StageStamp date={bill.approved_date} by={nameFor(bill.approved_by)} />} />
          <Row label="Transmitted" value={<StageStamp date={bill.transmitted_date} by={nameFor(bill.transmitted_by)} />} />
          <Row label="Paid" value={<StageStamp date={bill.payment_date} by={nameFor(bill.paid_by)} />} />
          {bill.status === 'void' && <Row label="Voided" value={<StageStamp date={bill.voided_at} by={nameFor(bill.voided_by)} />} />}
        </InfoCard>
        <InfoCard label={bill.status === 'void' ? 'Void reason' : 'Payment'}>
          {bill.status === 'void'
            ? (bill.void_reason
                ? <p style={{ margin: 0, fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap' }}>{bill.void_reason}</p>
                : <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>No reason recorded.</p>)
            : bill.status === 'paid'
              ? <>
                  <Row label="Amount" value={money(bill.payment_amount)} />
                  <Row label="Date" value={formatDate(bill.payment_date)} />
                  <Row label="Method" value={bill.payment_method || '—'} />
                  <Row label="Reference" value={bill.payment_reference || '—'} />
                </>
              : <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>No payment recorded.</p>}
        </InfoCard>
      </div>

      {/* C3 — the transmittal lives on the tax-bill profile itself: status + a link to
          the full transmittal + a shareable/not-shareable toggle. It's already filed in
          the client's All Documents; sharing pushes it to the client portal. */}
      {transmittalQuery.data?.id && (
        <div style={{
          border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)',
          background: 'var(--color-card)', padding: 'var(--sp-4) var(--sp-5)', margin: 'var(--sp-4) 0',
        }}>
          <div style={{
            fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
            letterSpacing: 'var(--ls-wider)', color: 'var(--color-muted)', marginBottom: 'var(--sp-3)',
          }}>Transmittal</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
              <Badge variant="neutral">{String(transmittalQuery.data.status || '').replace(/_/g, ' ')}</Badge>
              <a
                onClick={() => navigate(`/transmittals/${transmittalQuery.data!.id}`)}
                style={{ color: 'var(--brand-primary)', cursor: 'pointer', fontWeight: 'var(--fw-medium)', fontSize: 'var(--fs-sm)' }}
              >View transmittal ↗</a>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              <Badge variant={transmittalQuery.data.shared ? 'success' : 'neutral'} dot>
                {transmittalQuery.data.shared ? 'Shared to portal' : 'Not shared'}
              </Badge>
              {transmittalQuery.data.document_id && (
                <Button
                  variant="secondary" size="sm" loading={shareMut.isPending}
                  onClick={() => shareMut.mutate({ docId: transmittalQuery.data!.document_id!, share: !transmittalQuery.data!.shared })}
                >{transmittalQuery.data.shared ? 'Unshare' : 'Share to portal'}</Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* A0 — the bill's OWN documents, on the bill screen. The bill PDF is the thing
          a person looking at a bill wants next, and it used to require navigating to
          the documents area and filtering back down to this bill. Reads #266's tax-bill
          linkage surface (documents.tax_bill_id via /api/workflow, book-scoped), and a
          row's bytes are one click away — no navigation. */}
      <div style={{
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        background: 'var(--color-card)',
        padding: 'var(--sp-4) var(--sp-5)',
        margin: 'var(--sp-4) 0',
      }}>
        <div style={{
          fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-muted)', marginBottom: 'var(--sp-3)',
        }}>Documents</div>
        <LinkedDocumentsPanel target="tax-bills" targetId={bill.id} />
      </div>

      {/* Notes — the transmittal / approval-sheet thread for this bill (e.g. a
          supplemental bill where the client already paid a prior amount, the
          differential recorded here). Same reusable EntityNotesPanel + @mentions
          the gov profiles / client surface, keyed to entity_type 'tax_bills'. */}
      <div style={{
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        background: 'var(--color-card)',
        padding: 'var(--sp-4) var(--sp-5)',
        margin: 'var(--sp-4) 0',
      }}>
        <div style={{
          fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-muted)', marginBottom: 'var(--sp-3)',
        }}>Notes</div>
        <EntityNotesPanel entityType="tax_bills" entityId={bill.id} />
      </div>

      <div style={{ marginTop: 'var(--sp-5)' }}>
        <EntityGroupsPanel entityType="tax_bill" entityId={bill.id} />
      </div>

      <CreateEntityModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        table="tax_bills"
        routePath="tax-bills"
        recordId={bill.id}
        invalidateKeys={[['tax-bills-list'], ['tax_bills', bill.id]]}
      />

      {/* C1 — catch-up pop-up: after a transmit, offer to also transmit the client's OTHER
          approved same-state bills. Each files its own (collector-grouped) transmittal;
          a single combined document across DIFFERENT collectors is the Part V bill redesign. */}
      <Modal open={!!catchUp} onClose={finishCatchUp} title={`Catch up other ${catchUp?.state ?? ''} bills?`} size="sm">
        <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
          This client has <strong>{catchUp?.bills.length}</strong> other approved {catchUp?.state} tax {catchUp?.bills.length === 1 ? 'bill' : 'bills'} not yet transmitted. Transmit the selected ones now too — each files its own transmittal. Leave them and stay one-to-one if you prefer.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', maxHeight: 240, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)', marginBottom: 'var(--sp-4)' }}>
          {catchUp?.bills.map((b) => (
            <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
              <input
                type="checkbox"
                checked={catchUpSel.has(b.id)}
                onChange={() => setCatchUpSel((prev) => { const n = new Set(prev); if (n.has(b.id)) n.delete(b.id); else n.add(b.id); return n; })}
              />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {b.property_name} · {b.account_number}{b.collector_name ? ` · ${b.collector_name}` : ''}
              </span>
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button variant="secondary" size="sm" disabled={catchUpMut.isPending} onClick={finishCatchUp}>Not now</Button>
          <Button
            variant="primary" size="sm" loading={catchUpMut.isPending} disabled={catchUpSel.size === 0}
            onClick={() => catchUpMut.mutate([...catchUpSel])}
          >Transmit selected{catchUpSel.size ? ` (${catchUpSel.size})` : ''}</Button>
        </div>
      </Modal>

      {/* C3 — share-on-create pop-up: after Transmit files the (not-yet-shared) transmittal,
          offer to push it to the client portal now, or keep it private to review first. */}
      <Modal open={!!shareOnCreate} onClose={() => setShareOnCreate(null)} title="Transmittal created" size="sm">
        <p style={{ margin: '0 0 var(--sp-4)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
          The transmittal PDF was created and filed in the client's documents. Share it to the client
          portal now, or keep it private to review first — you can share it any time from this bill.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button variant="secondary" size="sm" onClick={() => setShareOnCreate(null)}>Keep private</Button>
          <Button
            variant="primary" size="sm" loading={shareMut.isPending} disabled={!shareOnCreate?.document_id}
            onClick={() => {
              if (shareOnCreate?.document_id) {
                shareMut.mutate({ docId: shareOnCreate.document_id, share: true }, { onSuccess: () => setShareOnCreate(null) });
              }
            }}
          >Share now</Button>
        </div>
      </Modal>

      {/* §7 — edit the collector's mailing address from the tax bill; the save
          writes back to the collector record itself. */}
      {collectorQuery.data && (
        <CollectorAddressModal
          open={collectorAddrOpen}
          collector={collectorQuery.data}
          onClose={() => setCollectorAddrOpen(false)}
          onSaved={() => {
            setCollectorAddrOpen(false);
            queryClient.invalidateQueries({ queryKey: ['collector', bill.collector_id] });
            toast({ variant: 'success', message: 'Collector mailing address updated.' });
          }}
        />
      )}

      <Modal
        open={!!confirmAction}
        onClose={() => { if (!mutation.isPending) setConfirmAction(null); }}
        title={confirmAction ? `Confirm: ${nextAction[bill.status]?.label}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmAction(null)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button variant="primary" loading={mutation.isPending} onClick={() => confirmAction && mutation.mutate(confirmAction)}>
              {confirmAction ? nextAction[bill.status]?.label : ''}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          This will move the bill from <strong>{bill.status.replace(/_/g, ' ')}</strong> to the next state and log an audit entry.
        </p>
      </Modal>

      <PayBillModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        billId={bill.id}
        defaultAmount={bill.tax_amount}
      />

      <VoidBillModal
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
        billId={bill.id}
        currentStatus={bill.status}
      />

      <IssueCorrectionModal
        open={correctionOpen}
        onClose={() => setCorrectionOpen(false)}
        billId={bill.id}
        onIssued={(newBillId) => { setCorrectionOpen(false); navigate(`/tax-bills/${newBillId}`); }}
      />
    </>
  );
}

function fieldLabel(text: string) {
  return (
    <span style={{
      display: 'block',
      marginBottom: 'var(--sp-1)',
      fontSize: 'var(--fs-sm)',
      fontWeight: 'var(--fw-medium)',
      color: 'var(--color-ink)',
    }}>{text}</span>
  );
}

function errMessage(err: unknown): string {
  return err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
    ? String((err.body as { error: unknown }).error)
    : err instanceof Error ? err.message : String(err);
}

interface PayBillModalProps {
  open: boolean;
  onClose: () => void;
  billId: string;
  defaultAmount: number | null;
}

/**
 * Record a tax-bill payment. POST /api/workflow/tax-bills/:billId/pay.
 * payment_amount is domain dollars (NUMERIC) — sent as a plain number, no
 * cents conversion (cents live only at the Stripe boundary). Server CAS
 * (lifecycle-4) enforces the received/processed/approved → paid transition.
 */
function PayBillModal({ open, onClose, billId, defaultAmount }: PayBillModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [paymentDate, setPaymentDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState<string>(defaultAmount != null ? String(defaultAmount) : '');
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/workflow/tax-bills/${billId}/pay`, {
      payment_date: paymentDate,
      payment_amount: Number(amount),
      payment_method: method.trim() || null,
      payment_reference: reference.trim() || null,
    }),
    onSuccess: () => {
      toast({ variant: 'success', message: 'Payment recorded.' });
      queryClient.invalidateQueries({ queryKey: ['tax_bills', billId] });
      queryClient.invalidateQueries({ queryKey: ['tax-bills-list'] });
      onClose();
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const amountValid = amount.trim() !== '' && !isNaN(Number(amount));
  const canSubmit = paymentDate !== '' && amountValid && !mutation.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title="Record payment"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!canSubmit} onClick={() => canSubmit && mutation.mutate()}>
            Record payment
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          {fieldLabel('Payment date')}
          <Input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} required />
        </label>
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          {fieldLabel('Payment amount ($)')}
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="e.g. 4200.00"
            invalid={amount.trim() !== '' && !amountValid}
            required
          />
        </label>
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          {fieldLabel('Method (optional)')}
          <Input type="text" value={method} onChange={e => setMethod(e.target.value)} placeholder="check / ach / wire" />
        </label>
        <label style={{ display: 'block' }}>
          {fieldLabel('Reference (optional)')}
          <Input type="text" value={reference} onChange={e => setReference(e.target.value)} placeholder="confirmation / check #" />
        </label>
      </form>
    </Modal>
  );
}

interface VoidBillModalProps {
  open: boolean;
  onClose: () => void;
  billId: string;
  currentStatus: BillStatus;
}

/**
 * Void a tax bill. POST /api/workflow/tax-bills/:billId/void — requires a
 * void_reason. Server CAS (lifecycle-r5-1) enforces the non-terminal → void
 * transition (paid/void are terminal and rejected).
 */
function VoidBillModal({ open, onClose, billId, currentStatus }: VoidBillModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [reason, setReason] = useState('');

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/workflow/tax-bills/${billId}/void`, { void_reason: reason.trim() }),
    onSuccess: () => {
      toast({ variant: 'success', message: 'Bill voided.' });
      queryClient.invalidateQueries({ queryKey: ['tax_bills', billId] });
      queryClient.invalidateQueries({ queryKey: ['tax-bills-list'] });
      setReason('');
      onClose();
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const canSubmit = reason.trim() !== '' && !mutation.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title="Void tax bill"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button variant="danger" loading={mutation.isPending} disabled={!canSubmit} onClick={() => canSubmit && mutation.mutate()}>
            Void bill
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          Voiding moves the bill from <strong>{currentStatus.replace(/_/g, ' ')}</strong> to <strong>void</strong>. This is terminal and logs an audit entry.
        </p>
        <label style={{ display: 'block' }}>
          {fieldLabel('Void reason')}
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            required
            style={{
              width: '100%',
              padding: 'var(--sp-2) var(--sp-3)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-md)',
              background: 'var(--color-card)',
              color: 'var(--color-ink)',
              fontSize: 'var(--fs-md)',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
            placeholder="Why is this bill being voided?"
          />
        </label>
      </form>
    </Modal>
  );
}

// §4.7 — the read-only computation returned by ?preview=1, and again alongside the
// issued bill. Money fields arrive NULL for a money-blind requester (the route nulls
// them), which is why every figure renders through `money()`/`num()` rather than raw.
interface CorrectionLine {
  jurisdictionId: string | null;
  originalTax: number | null;
  settledTax: number | null;
  amount: number | null;
  taxableValue: number | null;
  taxRate: number | null;
}
interface CorrectionPreview {
  kind: 'corrected' | 'refund';
  dueDate: string | null;
  dueDatePassed: boolean;
  finalValueDate: string | null;
  originalBilledAssessedValue: number | null;
  settledAssessedValue: number | null;
  originalTotal: number | null;
  newTotal: number | null;
  lines: CorrectionLine[];
}
interface IssueCorrectionResult { kind: 'corrected' | 'refund'; bill: { id: string } }

/** Whole numbers with separators, for assessed/taxable values (not dollars). */
const num = (v: number | null | undefined) => (v != null ? Math.round(v).toLocaleString() : '—');

/** Signed dollars — a refund line is negative and must READ as negative, not as a magnitude. */
const signedMoney = (v: number | null | undefined) =>
  v == null ? '—' : v < 0 ? `($${Math.abs(Math.round(v)).toLocaleString()})` : `$${Math.round(v).toLocaleString()}`;

interface IssueCorrectionModalProps {
  open: boolean;
  onClose: () => void;
  billId: string;
  onIssued: (newBillId: string) => void;
}

/**
 * §4.7 — issue a CORRECTED or REFUND bill against this (transmitted/paid) bill.
 * POST /api/workflow/tax-bills/:billId/issue-correction, previewed with ?preview=1.
 *
 * The operator does NOT choose the branch: the server computes it from the data
 * (a paid bill always refunds; an unpaid transmitted bill corrects unless the final
 * value landed after the due date). So the modal PREVIEWS first and states which
 * kind it will issue and why — issuing a refund when you expected a corrected
 * statement is a money event, not a UI surprise.
 *
 * Neither kind edits the original: a corrected bill carries the FULL settled amounts
 * (not a delta) and marks the original "See Corrected Statement"; a refund carries
 * NEGATIVE per-jurisdiction deltas and leaves the original untouched.
 */
function IssueCorrectionModal({ open, onClose, billId, onIssued }: IssueCorrectionModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Preview is fetched only while the modal is open — it is a POST, so it must not
  // fire on mount behind a closed dialog.
  const previewQuery = useQuery<{ preview: CorrectionPreview }>({
    queryKey: ['bill-correction-preview', billId],
    queryFn: () => api.post<{ preview: CorrectionPreview }>(
      `/api/workflow/tax-bills/${billId}/issue-correction?preview=1`, {}),
    enabled: open,
    staleTime: 0,
  });

  const mutation = useMutation<IssueCorrectionResult, Error, void>({
    mutationFn: () => api.post<IssueCorrectionResult>(`/api/workflow/tax-bills/${billId}/issue-correction`, {}),
    onSuccess: (res) => {
      toast({
        variant: 'success',
        message: res.kind === 'refund' ? 'Refund bill issued.' : 'Corrected bill issued.',
      });
      // The ORIGINAL changes too (a corrected bill marks it superseded), and the
      // parcel-year now carries an extra bill — so refresh both ends, not just the list.
      queryClient.invalidateQueries({ queryKey: ['tax_bills', billId] });
      queryClient.invalidateQueries({ queryKey: ['bill-corrections-of', billId] });
      queryClient.invalidateQueries({ queryKey: ['tax-bills-list'] });
      queryClient.invalidateQueries({ queryKey: ['tax-bill-collector-groups'] });
      onIssued(res.bill.id);
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const preview = previewQuery.data?.preview;
  const isRefund = preview?.kind === 'refund';

  // A bill with no detail lines has NO snapshot to correct against, and the service
  // refuses it outright. Without this the modal renders a confident "total $0" off an
  // empty line set and offers a submit the server will reject — a wrong figure and a
  // dead button. `originalTotal` is null for exactly this case (the service reports
  // null rather than a wrong original), so unknown never renders as zero.
  const noSnapshot = !!preview && preview.lines.length === 0;
  const canIssue = !!preview && !noSnapshot && !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title={preview ? (isRefund ? 'Issue refund bill' : 'Issue corrected bill') : 'Issue correction'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!canIssue}
            onClick={() => canIssue && mutation.mutate()}
          >
            {isRefund ? 'Issue refund bill' : 'Issue corrected bill'}
          </Button>
        </>
      }
    >
      {previewQuery.isLoading && (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Computing the correction…</p>
      )}
      {previewQuery.error && (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-status-red)' }}>
          {errMessage(previewQuery.error)}
        </p>
      )}

      {noSnapshot && (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
          This bill carries no per-jurisdiction line items, so there is no snapshot to
          correct against and no correction can be issued. Add the bill's jurisdiction
          lines first — a correction is computed from them, not from the bill total.
        </p>
      )}

      {preview && !noSnapshot && (
        <>
          <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
            {isRefund ? (
              <>
                The bill was already paid or its due date passed, so this issues a{' '}
                <strong>refund bill</strong> with negative per-jurisdiction lines. The original
                is left untouched.
              </>
            ) : (
              <>
                The final value landed before the due date, so this issues a{' '}
                <strong>corrected bill</strong> carrying the full settled amounts — not a delta.
                The original keeps its amounts and is marked{' '}
                <strong>See Corrected Statement</strong>.
              </>
            )}
          </p>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
            gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)',
          }}>
            <SummaryCell label="Original total" value={signedMoney(preview.originalTotal)} />
            <SummaryCell label={isRefund ? 'Refund total' : 'Corrected total'} value={signedMoney(preview.newTotal)} strong />
            <SummaryCell label="Assessed as billed" value={num(preview.originalBilledAssessedValue)} />
            <SummaryCell label="Settled assessed" value={num(preview.settledAssessedValue)} />
            <SummaryCell
              label="Due date"
              value={preview.dueDate ? formatDate(preview.dueDate) : '—'}
              caption={isRefund ? 'refund is mailed, not due' : 'unchanged'}
            />
            <SummaryCell
              label="Final value dated"
              value={preview.finalValueDate ? formatDate(preview.finalValueDate) : '—'}
              caption={preview.dueDatePassed ? 'after the due date' : 'before the due date'}
            />
          </div>

          <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', marginBottom: 'var(--sp-2)' }}>
            Per-jurisdiction lines ({preview.lines.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
              <thead>
                <tr style={{ textAlign: 'right', color: 'var(--color-muted)' }}>
                  <th style={{ textAlign: 'left', padding: 'var(--sp-1) var(--sp-2)' }}>Jurisdiction</th>
                  <th style={{ padding: 'var(--sp-1) var(--sp-2)' }}>Original tax</th>
                  <th style={{ padding: 'var(--sp-1) var(--sp-2)' }}>Settled tax</th>
                  <th style={{ padding: 'var(--sp-1) var(--sp-2)' }}>{isRefund ? 'Refund' : 'Billed'}</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((l, i) => (
                  <tr key={l.jurisdictionId || i} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: 'var(--sp-2)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>
                      {l.jurisdictionId || '—'}
                    </td>
                    <td style={{ padding: 'var(--sp-2)', textAlign: 'right' }}>{signedMoney(l.originalTax)}</td>
                    <td style={{ padding: 'var(--sp-2)', textAlign: 'right' }}>{signedMoney(l.settledTax)}</td>
                    <td style={{ padding: 'var(--sp-2)', textAlign: 'right', fontWeight: 'var(--fw-semibold)' }}>
                      {signedMoney(l.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

/** §4.7 — an inline banner tying a bill to the other half of its corrected/refund pair. */
function CorrectionNotice({ text, linkLabel, onClick }: {
  text: string; linkLabel: string; onClick: () => void;
}) {
  return (
    <div style={{
      border: '1px solid var(--color-border)',
      borderLeft: '3px solid var(--brand-primary)',
      borderRadius: 'var(--r-lg)',
      background: 'var(--color-card)',
      padding: 'var(--sp-3) var(--sp-4)',
      margin: 'var(--sp-4) 0',
      display: 'flex',
      flexWrap: 'wrap',
      gap: 'var(--sp-2)',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: 'var(--fs-sm)',
    }}>
      <span style={{ color: 'var(--color-ink)' }}>{text}</span>
      <LinkText label={linkLabel} onClick={onClick} />
    </div>
  );
}

/** One labelled figure in the correction preview's summary grid. */
function SummaryCell({ label, value, caption, strong }: {
  label: string; value: string; caption?: string; strong?: boolean;
}) {
  return (
    <div>
      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>{label}</div>
      <div style={{
        fontSize: 'var(--fs-md)',
        fontWeight: strong ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        color: 'var(--color-ink)',
      }}>{value}</div>
      {caption && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{caption}</div>}
    </div>
  );
}

// §7 — a focused editor for the collector's mailing address, opened from the tax
// bill. The PUT targets /api/collectors/:id, so the edit updates the collector
// record itself (and every bill on that collector reflects it).
function CollectorAddressModal({ open, collector, onClose, onSaved }: {
  open: boolean;
  collector: Collector;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [line1, setLine1] = useState(collector.mailing_address_line1 ?? '');
  const [line2, setLine2] = useState(collector.mailing_address_line2 ?? '');
  const [city, setCity] = useState(collector.mailing_city ?? '');
  const [state, setState] = useState(collector.mailing_state ?? '');
  const [postal, setPostal] = useState(collector.mailing_postal_code ?? '');

  // Re-seed from the collector whenever a different one opens (or it refetches).
  useEffect(() => {
    setLine1(collector.mailing_address_line1 ?? '');
    setLine2(collector.mailing_address_line2 ?? '');
    setCity(collector.mailing_city ?? '');
    setState(collector.mailing_state ?? '');
    setPostal(collector.mailing_postal_code ?? '');
  }, [collector]);

  const save = useMutation({
    mutationFn: () => api.put(`/api/collectors/${collector.id}`, {
      mailing_address_line1: line1 || null,
      mailing_address_line2: line2 || null,
      mailing_city: city || null,
      mailing_state: state || null,
      mailing_postal_code: postal || null,
    }),
    onSuccess: () => onSaved(),
    onError: (err) => toast({
      variant: 'error',
      message: err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : 'Could not update the collector address.',
    }),
  });

  return (
    <Modal
      open={open}
      onClose={() => { if (!save.isPending) onClose(); }}
      title={`Edit mailing address — ${collector.name}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save address</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
        <label>{fieldLabel('Address line 1')}<Input value={line1} onChange={(e) => setLine1(e.target.value)} /></label>
        <label>{fieldLabel('Address line 2')}<Input value={line2} onChange={(e) => setLine2(e.target.value)} /></label>
        <label>{fieldLabel('City')}<Input value={city} onChange={(e) => setCity(e.target.value)} /></label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <label>{fieldLabel('State')}<Select options={STATE_SELECT_OPTIONS} value={state} onChange={(e) => setState(e.target.value)} /></label>
          {/* B2 / D — the zip field accepts numbers only (five digits). */}
          <label>{fieldLabel('ZIP')}<Input value={postal} inputMode="numeric" maxLength={5} placeholder="12345" onChange={(e) => setPostal(e.target.value.replace(/\D/g, '').slice(0, 5))} /></label>
        </div>
        <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
          Saving updates the collector record — every tax bill on this collector reflects the change.
        </p>
      </div>
    </Modal>
  );
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-3)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0',
      fontSize: 'var(--fs-sm)',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{
        color: 'var(--color-ink)',
        fontWeight: 'var(--fw-medium)',
        textAlign: 'right',
        wordBreak: 'break-word',
        maxWidth: '60%',
      }}>{value}</span>
    </div>
  );
}
